import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { Autocomplete, Chip, Divider, Tab, Tabs, TextField } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";
import MasterCommonTable from "../../UiComponents/CommonTable/MasterCommonTable";
import Loader from "../../UiComponents/Loader/Loader";
import ConfirmBox from "../../UiComponents/ConfirmBox/ConfirmBox";
import { SearchIcon, DoneOutlineIcon, NotInterestedIcon, AddIcon } from "../../Resources/Icons/icons";
import { GetPolicyWalletList, UpdatePolicyWalletApproval, getReceiptImage } from "../../Service/_index";
import ImageViewerDialog from "../UserWallet/components/ImageViewerDialog";
import TopupSource, { isAdminTopup } from "../AddMoneyToUser/TopupSource";
import {
  canAddMoneyToUsers,
  formatINR,
  isClientUserType,
  isPdfKey,
  proofKeyOf,
  proofNameOf,
} from "../AddMoneyToUser/addMoneyUtils";
import { inputSx } from "../AddMoneyToUser/parts";
import { ToastError } from "../../UiComponents/Toaster/Toast";
import SuccessImage from "../../Resources/Images/ActiveImage.png";
import FailureImage from "../../Resources/Images/disable.png";
import UserWalletsTab from "./UserWalletsTab";

const getStatusColor = (status) => {
  switch (status) {
    case "APPROVED":
      return "green";
    case "REJECTED":
      return "red";
    default:
      return "orange";
  }
};

const AdminPolicyWallet = () => {
  const navigate = useNavigate();
  const [originalData, setOriginalData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [userFilter, setUserFilter] = useState(null);
  const [adminFilter, setAdminFilter] = useState(null); // the super admin's list only (rows carry adminName)
  const [openLoader, setOpenLoader] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [imageViewDialog, setImageViewDialog] = useState({ open: false, imageUrl: "", fileName: "" });
  const [confirmBox, setConfirmBox] = useState({ open: false, row: null, status: "" });
  const [tab, setTab] = useState("wallets"); // "wallets" (per-user balances) | "requests" (top-up requests)

  // The accountant shares this menu and may look, but only the admin (CLIENT) may
  // approve or reject; those calls are refused for anyone else.
  const canDecide = isClientUserType(sessionStorage.getItem("userType"));

  const GetData = () => {
    setOpenLoader(true);
    GetPolicyWalletList()
      .then((res) => {
        setOpenLoader(false);
        if (res && !res.error && Array.isArray(res)) {
          setOriginalData(res.map((row, index) => ({ ...row, __index__: index + 1 })));
        } else {
          setOriginalData([]);
        }
        setPage(0);
      })
      .catch(() => {
        setOpenLoader(false);
        setOriginalData([]);
      });
  };

  useEffect(() => {
    GetData();
  }, []);

  // Users that have at least one row, so the filter only offers people who can match.
  // A user picked from the User Wallets tab stays selectable even with no rows yet.
  // Only the super admin's list carries adminName (every admin's users together), like the User Wallets tab.
  const showAdmin = useMemo(() => originalData.some((row) => row.adminName !== undefined), [originalData]);
  const adminOptions = useMemo(() => {
    const seen = new Map();
    originalData.forEach((row) => {
      if (row.clientId && !seen.has(String(row.clientId))) {
        seen.set(String(row.clientId), { id: String(row.clientId), name: row.adminName || "Unknown admin" });
      }
    });
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [originalData]);

  const userOptions = useMemo(() => {
    const seen = new Map();
    originalData.forEach((row) => {
      if (adminFilter && String(row.clientId) !== adminFilter.id) return;
      if (row.userId && !seen.has(String(row.userId))) {
        seen.set(String(row.userId), { id: String(row.userId), name: row.userName || "Unknown user" });
      }
    });
    if (userFilter && !seen.has(userFilter.id)) seen.set(userFilter.id, userFilter);
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [originalData, userFilter, adminFilter]);

  const requests = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return originalData
      .filter((item) => {
        if (adminFilter && String(item.clientId) !== adminFilter.id) return false;
        if (userFilter && String(item.userId) !== userFilter.id) return false;
        if (!term) return true;
        return `${item.userName} ${item.adminName || ""} ${item.amount} ${item.isApproved} ${item.remarks || ""} ${item.reference || ""}`
          .toLowerCase()
          .includes(term);
      })
      // S.No counts the rows on screen, not the position in the full list.
      .map((item, i) => ({ ...item, __index__: i + 1 }));
  }, [originalData, searchTerm, userFilter, adminFilter]);

  const pendingRequests = useMemo(
    () => originalData.filter((row) => row.isApproved === "PENDING").length,
    [originalData]
  );

  const onSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const onUserFilter = (option) => {
    setUserFilter(option);
    setPage(0);
  };

  const onAdminFilter = (option) => {
    setAdminFilter(option);
    // a user picked under another admin no longer fits
    if (option && userFilter && !originalData.some((r) => String(r.userId) === userFilter.id && String(r.clientId) === option.id)) {
      setUserFilter(null);
    }
    setPage(0);
  };

  // The policy a debit was for, in the same tab (like the Company Wallet's "View Policy"). Only the admin gets this
  // link: the policy page has no role check of its own and shows customer KYC images and a Create Certificate button.
  const openPolicy = (policyId) => navigate(`/onlinePolicy/view/form/${policyId}`);

  // "Summary" on a user: that user's top-up requests.
  const showRequestsOf = (user) => {
    setSearchTerm("");
    setUserFilter(user);
    setPage(0);
    setTab("requests");
  };

  const handleViewProof = async (row) => {
    setOpenLoader(true);
    try {
      // Requests store {key, fileName}; admin top-ups store just the key.
      const key = proofKeyOf(row.proof);
      const response = await getReceiptImage(key);
      setOpenLoader(false);
      if (!response.error && response.url) {
        if (isPdfKey(key)) {
          window.open(response.url, "_blank", "noopener,noreferrer");
          return;
        }
        setImageViewDialog({
          open: true,
          imageUrl: response.url,
          fileName: proofNameOf(row.proof),
        });
      }
    } catch (error) {
      setOpenLoader(false);
    }
  };

  const handleDecision = (row, status) => {
    setConfirmBox({ open: true, row, status });
  };

  const confirmDecision = () => {
    const { row, status } = confirmBox;
    setConfirmBox({ open: false, row: null, status: "" });
    setOpenLoader(true);
    UpdatePolicyWalletApproval(row._id, status)
      .then((res) => {
        setOpenLoader(false);
        // The service only toasts network failures; {error:true} answers came back silent.
        if (res?.error) ToastError(res.message || "Could not update the request");
        GetData();
      })
      .catch(() => setOpenLoader(false));
  };

  const headCells = [
    { id: "sno", placeMent: true, disablePadding: false, label: "S.No", type: "index" },
    {
      id: "createdAt",
      placeMent: true,
      disablePadding: false,
      label: "Date",
      type: "component",
      renderComponent: (row) => (
        <Typography>{row.createdAt ? moment(row.createdAt).format("DD/MM/YYYY hh:mm A") : "-"}</Typography>
      ),
    },
    { id: "userName", placeMent: true, disablePadding: false, label: "User" },
    ...(showAdmin
      ? [
          {
            id: "adminName",
            placeMent: true,
            disablePadding: false,
            label: "Admin",
            type: "component",
            renderComponent: (row) => <Typography sx={{ fontFamily: "Inter" }}>{row.adminName || "—"}</Typography>,
          },
        ]
      : []),
    {
      id: "type",
      placeMent: true,
      disablePadding: false,
      label: "Type",
      type: "component",
      renderComponent: (row) => <TopupSource row={row} />,
    },
    {
      id: "amount",
      placeMent: true,
      disablePadding: false,
      label: "Top Up Amount",
      type: "component",
      renderComponent: (row) => (
        <Typography sx={{ color: "green", fontWeight: "500" }}>{formatINR(row.amount)}</Typography>
      ),
    },
    {
      id: "proof",
      placeMent: false,
      disablePadding: false,
      label: "Proof",
      type: "component",
      renderComponent: (row) => {
        if (proofKeyOf(row.proof)) {
          return (
            <IconButton size="small" onClick={() => handleViewProof(row)} sx={{ color: "#1E3A5F" }}>
              <VisibilityIcon />
            </IconButton>
          );
        }
        return <Typography variant="caption" color="text.secondary">-</Typography>;
      },
    },
    {
      id: "isApproved",
      placeMent: true,
      disablePadding: false,
      label: "Status",
      type: "coloredText",
      getColor: (row) => getStatusColor(row.isApproved),
    },
    {
      id: "action",
      placeMent: false,
      disablePadding: false,
      label: "Action",
      type: "component",
      renderComponent: (row) => {
        if (isAdminTopup(row)) {
          return <Typography variant="caption" color="text.secondary">-</Typography>;
        }
        if (row.isApproved === "PENDING" && !canDecide) {
          return <Typography color="warning.main">Pending approval</Typography>;
        }
        if (row.isApproved === "PENDING") {
          return (
            <ButtonGroup size="small" variant="contained">
              <Button
                sx={{ backgroundColor: "#1E3A5F", "&:hover": { backgroundColor: "#2E5A8F" } }}
                onClick={() => handleDecision(row, "APPROVED")}
              >
                Accept
              </Button>
              <Button
                sx={{ backgroundColor: "#D4AF37", color: "#1E3A5F", "&:hover": { backgroundColor: "#C9A961" } }}
                onClick={() => handleDecision(row, "REJECTED")}
              >
                Reject
              </Button>
            </ButtonGroup>
          );
        }
        if (row.isApproved === "APPROVED") {
          return <Typography color="success.main">✓ Approved</Typography>;
        }
        return <Typography color="error.main">✗ Rejected</Typography>;
      },
    },
  ];

  const tableConfig = {
    isAction: false,
    isView: false,
    isEdit: false,
    isDelete: false,
    isSearch: false,
    isUpload: false,
    isFileDownload: false,
  };

  return (
    <>
      <Grid container className="Master_Card_Container">
        <Grid container alignItems={"center"} className="Master_Card_Sub_Container">
          <Grid item xs={12} sm={6} md={6}>
            <Typography className="Master_Card_Tittle">Policy Wallet</Typography>
          </Grid>
          {canAddMoneyToUsers() && (
            <Grid item xs={12} sm={6} md={6} className="d-flex justify-content-end">
              <Button
                className="Master_Card_create_Button w-100"
                startIcon={<AddIcon sx={{ marginRight: "1.5px" }} />}
                onClick={() => navigate("/addPolicyWalletMoney")}
              >
                Add Money
              </Button>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1 }} />
        </Grid>
        <Grid item xs={12} sx={{ px: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, next) => setTab(next)}
            aria-label="Policy wallet views"
            sx={{
              minHeight: 44,
              "& .MuiTabs-indicator": { backgroundColor: "#1E3A5F" },
              "& .MuiTab-root": { textTransform: "none", fontFamily: "Inter", fontWeight: 500, minHeight: 44 },
              "& .Mui-selected": { color: "#1E3A5F !important" },
            }}
          >
            <Tab value="wallets" label="User Wallets" />
            <Tab
              value="requests"
              label={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  Top-up Requests
                  {pendingRequests > 0 && (
                    <Chip
                      size="small"
                      label={pendingRequests}
                      aria-label={`${pendingRequests} pending`}
                      sx={{ height: 20, fontSize: "11px", color: "#B54708", backgroundColor: "#FFFAEB", border: "1px solid #FEDF89" }}
                    />
                  )}
                </span>
              }
            />
          </Tabs>
        </Grid>
        {tab === "wallets" ? (
          <UserWalletsTab onSummary={showRequestsOf} onOpenPolicy={canDecide ? openPolicy : undefined} />
        ) : (
          <>
            <Grid container alignItems={"center"} justifyContent={"flex-end"} columnSpacing={2} className="Master_Card_Sub_Container">
              {showAdmin && (
                <Grid item xs={12} sm={4} md={2.8} mt={{ xs: 2, sm: 0, md: 0 }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    openOnFocus
                    options={adminOptions}
                    value={adminFilter}
                    onChange={(_, option) => onAdminFilter(option)}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, selected) => option.id === selected.id}
                    noOptionsText="No admins yet"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Filter by admin"
                        inputProps={{ ...params.inputProps, "aria-label": "Filter by admin" }}
                        sx={inputSx}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={5} md={3.5} mt={{ xs: 2, sm: 0, md: 0 }}>
                <Autocomplete
                  size="small"
                  fullWidth
                  openOnFocus
                  options={userOptions}
                  value={userFilter}
                  onChange={(_, option) => onUserFilter(option)}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, selected) => option.id === selected.id}
                  noOptionsText={userOptions.length ? "No user matches your search" : "No users yet"}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Filter by user"
                      inputProps={{ ...params.inputProps, "aria-label": "Filter by user" }}
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3.1} mt={{ xs: 2, sm: 0, md: 0 }}>
                <Input
                  focused="false"
                  className="w-100 Master_Card_Input"
                  value={searchTerm}
                  onChange={onSearch}
                  disableUnderline={true}
                  placeholder="Search in Policy Wallet"
                  startAdornment={
                    <InputAdornment position="start" sx={{ marginRight: "0px" }}>
                      <IconButton aria-label="search">
                        <SearchIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <MasterCommonTable
                tableConfig={tableConfig}
                data={requests}
                columns={headCells}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                loading={openLoader}
                paginationReset={`${searchTerm}|${userFilter?.id || ""}`}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Loader open={openLoader} />

      <ImageViewerDialog
        open={imageViewDialog.open}
        onClose={() => setImageViewDialog({ open: false, imageUrl: "", fileName: "" })}
        imageUrl={imageViewDialog.imageUrl}
        fileName={imageViewDialog.fileName}
      />

      <ConfirmBox
        open={confirmBox.open}
        title={confirmBox.status === "APPROVED" ? "Accept" : "Reject"}
        content={`Are you sure you want to ${confirmBox.status === "APPROVED" ? "accept" : "reject"} this topup request?`}
        confirmButton={confirmBox.status === "APPROVED" ? "Accept" : "Reject"}
        setOpenConfirmBox={(val) => setConfirmBox((prev) => ({ ...prev, open: val }))}
        Function={confirmDecision}
        icon={confirmBox.status === "APPROVED" ? <DoneOutlineIcon /> : <NotInterestedIcon />}
        img={confirmBox.status === "APPROVED" ? SuccessImage : FailureImage}
      />
    </>
  );
};

export default AdminPolicyWallet;
